/**
 * App-level error boundary. Catches render-time exceptions so a single
 * broken screen doesn't take the whole kiosk down. Recovery action is
 * hard-navigation back to root, which also resets transient screen state.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';

import { translations } from '../i18n/translations';
import { useKioskStore } from '../store/kioskStore';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface to console for ops; the kiosk doesn't run a remote tracker.
    console.error('[KioskErrorBoundary]', error, info.componentStack);
  }

  private handleReset = (): void => {
    window.location.assign('/');
  };

  render(): ReactNode {
    if (this.state.error) {
      const t = translations[useKioskStore.getState().language];

      return (
        <div className="min-h-screen flex flex-col items-center justify-center gap-6 p-12 text-white bg-slate-900">
          <h1 className="text-3xl font-bold">{t.error.boundaryTitle}</h1>
          <p className="text-white/70 text-center max-w-md">{t.error.boundaryMessage}</p>
          <pre className="text-xs text-white/40 max-w-2xl whitespace-pre-wrap">
            {this.state.error.message}
          </pre>
          <button
            type="button"
            onClick={this.handleReset}
            className="px-6 py-3 rounded-full bg-white text-slate-900 font-semibold"
          >
            {t.error.boundaryBackHome}
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
